import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import clsx from 'clsx';
import { Activity, BookOpen, BookOpenText, ClipboardList, LineChart, ScrollText, Users } from 'lucide-react';
import { useMe } from '../../lib/auth.js';
import { consoleSections, type ConsoleSectionPath } from '../../lib/consoleSections.js';
import { AppShell } from './AppShell.js';

/**
 * The console is the same UI for admins and instructors, but each person only
 * sees the sections they can use: a trainer or course admin is never shown
 * People, Audit or Observability, and Guides appears only to guide writers.
 * What they can do inside a visible section is still decided by the server.
 */
export function AdminShell({ children }: { children: ReactNode }) {
  const me = useMe();
  const { isAdmin, isGuideWriter } = me.capabilities;
  // A guide writer with nothing to teach or run has Guides and nothing else.
  const authorsCourses =
    isAdmin ||
    me.capabilities.isInstructor ||
    me.capabilities.isCourseAdmin;

  const items = consoleSections(me.capabilities).map((section) => ({
    ...section,
    ...SECTION_NAV[section.to],
  }));

  return (
    <AppShell>
      <div className="mx-auto max-w-[92rem] px-4 sm:px-6 py-7">
        {/* grid-cols-1 is load-bearing: without it the single mobile column is
            auto-sized, and the rail's horizontally scrollable nav drags the
            track out to its own max-content (~485px) on a 320px screen. */}
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[13.5rem_minmax(0,1fr)]">
          <aside className="lg:sticky lg:top-20 lg:self-start">
            <p className="eyebrow mb-2 px-2.5">Authoring console</p>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto" aria-label="Console">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    clsx(
                      'relative flex items-center gap-2 rounded-control px-2.5 py-2 text-sm whitespace-nowrap',
                      'transition-colors duration-150 ease-out',
                      'lg:before:absolute lg:before:left-0 lg:before:inset-y-1.5 lg:before:w-0.5 lg:before:rounded-full',
                      isActive
                        ? 'bg-surface-2 text-ink font-semibold lg:before:bg-brand'
                        : 'text-ink-muted font-medium hover:text-ink hover:bg-surface-2 lg:before:bg-transparent',
                    )
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {!isAdmin && !authorsCourses && isGuideWriter ? (
              <p className="mt-4 px-2.5 text-xs text-ink-muted leading-relaxed border-l-2 border-line pl-3">
                You are a guide writer: you can write guides and edit your own.
              </p>
            ) : !isAdmin && me.capabilities.isCourseAdmin ? (
              <p className="mt-4 px-2.5 text-xs text-ink-muted leading-relaxed border-l-2 border-line pl-3">
                You are a course admin: you can run the courses you were given end to end.
                Ask an admin for anything else you need.
              </p>
            ) : !isAdmin ? (
              <p className="mt-4 px-2.5 text-xs text-ink-muted leading-relaxed border-l-2 border-line pl-3">
                You are an instructor on{' '}
                {me.capabilities.assignedSessionCount === 1
                  ? '1 session'
                  : `${me.capabilities.assignedSessionCount} sessions`}
                {me.capabilities.assignedCapstoneCount > 0 ? ' and a capstone' : ''}. Ask an
                admin if you need access to anything else.
              </p>
            ) : null}
          </aside>

          <div className="min-w-0">{children}</div>
        </div>
      </div>
    </AppShell>
  );
}

const SECTION_NAV: Record<ConsoleSectionPath, { label: string; icon: ReactNode }> = {
  '/admin/courses': { label: 'Courses & sessions', icon: <BookOpen size={15} /> },
  '/admin/guides': { label: 'Guides', icon: <BookOpenText size={15} /> },
  '/admin/users': { label: 'People', icon: <Users size={15} /> },
  '/admin/analytics': { label: 'Analytics', icon: <LineChart size={15} /> },
  '/admin/audit': { label: 'Audit log', icon: <ScrollText size={15} /> },
  '/admin/observability': { label: 'Observability', icon: <Activity size={15} /> },
};

export function AdminSectionHeading({
  title,
  description,
  actions,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div className="min-w-0 flex items-start gap-3">
        {icon ? (
          <span className="mt-1 text-ink-muted shrink-0" aria-hidden>
            {icon}
          </span>
        ) : (
          <span className="mt-1 text-ink-muted shrink-0" aria-hidden>
            <ClipboardList size={18} />
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink wrap-anywhere">{title}</h1>
          {description ? (
            <p className="text-sm text-ink-muted mt-1 max-w-prose leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
    </div>
  );
}
